import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { PageLoader } from '@lms/ui';
import { useAuth } from '@/hooks/useAuth';
import { resolveAuthenticatedHomePath } from '@/lib/student-contract-gate';

interface ProtectedRouteProps {
  redirectTo?: string;
}

function resolveGuestRedirectPath(homePath: string, fromPathname?: string): string {
  if (!fromPathname || fromPathname === '/login' || fromPathname.startsWith('/register')) {
    return homePath;
  }
  if (!fromPathname.startsWith('/') || fromPathname.startsWith('//')) {
    return homePath;
  }
  return fromPathname;
}

export function ProtectedRoute({ redirectTo = '/login' }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, isHydrated } = useAuth();
  const location = useLocation();

  if (!isHydrated || isLoading) {
    return <PageLoader label="Verificando sesión…" />;
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  return <Outlet />;
}

export function GuestRoute() {
  const location = useLocation();
  const { isAuthenticated, isHydrated, isLoading } = useAuth();
  const [homePath, setHomePath] = useState('/dashboard');
  const [homeResolved, setHomeResolved] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setHomeResolved(true);
      return;
    }

    setHomeResolved(false);
    resolveAuthenticatedHomePath()
      .then((path) => setHomePath(path))
      .finally(() => setHomeResolved(true));
  }, [isAuthenticated]);

  if (!isHydrated || isLoading || (isAuthenticated && !homeResolved)) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    const fromPathname = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    return <Navigate to={resolveGuestRedirectPath(homePath, fromPathname)} replace />;
  }

  return <Outlet />;
}
